import React from 'react';
import { ISO8601DateTimeString } from '@autopylot-internal/tiles-client';
import { dayjs } from './dayjs.ts';
import tzLookup from 'tz-lookup';
import { ICoordinate } from '../model/ICoordinate.ts';
import coordinatesFormatter from 'formatcoords';
import { renderToStaticMarkup } from 'react-dom/server';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export const formatElementAsText = (element: React.ReactElement) => {
    const html = renderToStaticMarkup(element);
    return html.replace(/<[^>]*>/g, '');
};

export const formatDollarsAndCents = (amount?: number) => {
    return (amount === undefined ? '' : currencyFormatter.format(amount));
};

export const formatDollars = (amount?: number) => {
    const currency = formatDollarsAndCents(amount);
    return currency.substring(0, currency.length - 3);
};

// return timezone id (e.g. 'America/New_York') for given lat/lng
export const getTimezone = (location: ICoordinate) => {
    // eslint-disable-next-line prefer-const
    let { lat, lng } = location;

    // mapbox can return out-of-range lng values near the date line,
    // but since we don't allow missions to be created there,
    // it only causes problems for this method, so patch it here
    if (lng < -180 || lng > 180) {
        lng = (lng % 360 + 540) % 360 - 180;
    }

    return tzLookup(lat, lng);
};

// return "Today", "Yesterday", "Tomorrow", or format like "Mon May 23" for all other dates
export const formatLocalDay = ({ date, location }: { date: string, location: ICoordinate }) => {
    const tz = getTimezone(location);
    const dateMoment = dayjs(date).tz(tz).startOf('d');
    const today = dayjs().tz(tz).startOf('d');
    const diff = dateMoment.diff(today, 'd');
    return (diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : diff === -1 ? 'yesterday' : dateMoment.format('ddd MMM D'));
};

// return local formatted date (e.g. "Mon Nov 17, 2021")
export const formatLocalDate = ({ date, location, includeYear }: { date: string, location: ICoordinate, includeYear?: boolean }) => {
    return formatDate({ date, timezone: getTimezone(location), includeYear });
};

// return formatted date (e.g. "Mon Nov 17, 2021")
export const formatDate = ({ date, timezone, includeYear }: { date: string, timezone: string, includeYear?: boolean }) => {
    return dayjs(date).tz(timezone).format(`ddd MMM D${includeYear ? ', YYYY' : ''}`);
};

// return local formatted date without month (e.g. "Mon 17")
export const formatLocalDateShort = ({ date, location }: { date: string, location: ICoordinate }) => {
    const tz = getTimezone(location);
    return dayjs(date).tz(tz).format('ddd D');
};

// return local formatted time optionally with timezone (e.g. "9:30 am EDT")
export const formatLocalTime = ({ time, location, withTimeZone = false }: { time: string, location: ICoordinate, withTimeZone?: boolean }) => {
    return formatTime({ time, timezone: getTimezone(location), withTimeZone });
};

// return formatted time optionally with timezone (e.g. "9:30 am EDT")
export const formatTime = ({ time, timezone, withTimeZone = false }: { time: string, timezone: string, withTimeZone?: boolean }) => {
    const format = 'h:mm a' + (withTimeZone ? ' z' : '');
    return dayjs(time).tz(timezone).format(format);
};

// return local formatted time as hour only (e.g. "9 am")
export const formatLocalHour = ({ time, location, withTimeZone = false }: { time: string, location: ICoordinate, withTimeZone?: boolean }) => {
    const format = 'ha' + (withTimeZone ? ' z' : '');
    const tz = getTimezone(location);
    return dayjs(time).tz(tz).format(format);
};

// return local formatted time with date (e.g. 4/5/22 3:14 am EST)
export const formatLocalDateAndTime = ({ location, time, withTimeZone = false, noYear = false }: { location: ICoordinate, time: ISO8601DateTimeString, withTimeZone?: boolean, noYear?: boolean }): string => {
    const format = `M/D${noYear ? '' : '/YYYY'} h:mm a${withTimeZone ? ' z' : ''}`;
    const tz = getTimezone(location);
    return dayjs(time).tz(tz).format(format);
};

// return time with date formatted in browser timezone (e.g. 4/5/22 3:14 am EST, or "Yesterday/Today/Tomorrow" for the date)
// (this format is currently used only in the equipment log)
export const formatLogDateAndTime = (date: string) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateMoment = dayjs(date).tz(tz).startOf('d');
    const today = dayjs().tz(tz).startOf('d');
    const diff = dateMoment.diff(today, 'd');
    return (diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday' : dateMoment.format('MM/DD/YY'));
};

// return local formatted time range with timezone (e.g. "9:30 am - 10:30 am EDT")
export const formatLocalTimeRange = ({ startTime, endTime, location, withTimeZone = false }: { startTime: string, endTime: string, location: ICoordinate, withTimeZone?: boolean }) => {
    const start = formatLocalTime({ time: startTime, location });
    const end = formatLocalTime({ time: endTime, location, withTimeZone });
    return `${start} - ${end}`;
};

export const formatDuration = (durationMinutes: number) => {
    const duration = dayjs.duration(durationMinutes, 'minutes');
    const hours = duration.hours();
    const minutes = duration.minutes();
    let output = '';
    if (hours) {
        output += `${hours} h `;
    }
    if (minutes) {
        output += `${minutes} m `;
    }
    return output.trim();
};

export const formatPhone = (phone: string) => {
    if (phone.length === 10) {
        return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
    }
    return phone; // not recognized US format
};

export const formatCoordinates = ({ lat, lng }: ICoordinate) => {
    return coordinatesFormatter(lat, lng).format('ddX', { latLonSeparator: ', ', decimalPlaces: 3 });
};

export const round = (value: number, dp: number): number => {
    const divisor = 10 ** dp;
    return Math.round(value * divisor) / divisor;
};

// converts "HelloWorld" to "Hello World"
export const unCamelCase = (str: string) => {
    return str
        // insert a space between lower & upper
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // space before last upper in a sequence followed by lower
        .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3');
};

export const initCap = (s: string) => {
    return s[0].toUpperCase() + s.substring(1);
};
